import { Injectable, OnDestroy } from '@angular/core';
import { CustomCart } from '@cart/const';
import {
  CartActions,
  CustomMultiCartFacade,
  MultiCartSelectors,
  MultiCartState,
  StateWithMultiCart,
} from '@cart/data-access';
import { Store, select } from '@ngrx/store';
import {
  SiteContextParamsService,
  StatePersistenceService,
  StateUtils,
  StorageSyncType,
  WindowRef,
} from '@spartacus/core';
import { Observable, Subscription, fromEvent } from 'rxjs';
import { filter, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class MultiCartStatePersistenceService implements OnDestroy {
  private STORAGE_KEY = 'cart';
  protected subscription = new Subscription();

  constructor(
    protected statePersistenceService: StatePersistenceService,
    protected siteContextParamsService: SiteContextParamsService,
    private store: Store<StateWithMultiCart>,
    private customMultiCartFacade: CustomMultiCartFacade,
    private windowRef: WindowRef,
  ) {}

  initSync(scope?: string): void {
    if (scope === 'cmsComponents') {
      this.subscription.add(
        this.statePersistenceService.syncWithStorage({
          key: this.STORAGE_KEY,
          state$: this.getCartState(),
          onRead: (state: MultiCartState) => this.onInitRead(state),
          storageType: StorageSyncType.LOCAL_STORAGE,
        }),
      );

      this.subscription.add(
        fromEvent(this.windowRef.document, 'visibilitychange').subscribe(() => {
          if (this.windowRef.document.visibilityState === 'visible') {
            const storageState = this.statePersistenceService.readStateFromStorage<MultiCartState>({
              key: this.STORAGE_KEY,
            });
            const state$ = this.getCartState();

            state$.pipe(take(1)).subscribe((state: MultiCartState) => {
              if (JSON.stringify(state) !== JSON.stringify(storageState)) {
                this.initCartState(storageState);
              }
            });
          }
        }),
      );
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private onInitRead(state?: MultiCartState): void {
    this.initCartState(state);
  }

  private getCartState(): Observable<MultiCartState> {
    return this.store.pipe(
      select(MultiCartSelectors.getMultiCartState),
      filter((state) => !!state),
    );
  }

  private initCartState(state?: MultiCartState): void {
    this.store.dispatch(new CartActions.ClearCartState());

    if (state) {
      if (state.active) {
        this.store.dispatch(new CartActions.SafelySetActiveCartId(state.active));
      } else {
        this.store.dispatch(new CartActions.SetActiveCartId(''));
      }
      if (state.carts?.entities) {
        Object.values(state.carts.entities)
          .filter(({ value }) => Boolean(value))
          .forEach(({ value }: StateUtils.LoaderState<CustomCart>) => {
            this.customMultiCartFacade.restoreCart(value, false);
          });
      }
    } else {
      this.store.dispatch(new CartActions.SetActiveCartId(''));
    }
  }
}
